import { CoreSteps } from '@components'
import { breakpoints } from '@constants'
import CompanyIcon from '@images/icons/company-register.svg?react'
import IndividualIcon from '@images/icons/individual-register.svg?react'
import ArrowIcon from '@images/icons/red-right-arrow.svg?react'
import parse from 'html-react-parser'
import i18n from 'i18next'
import { MerchantEntityTypes, parseFormToken, paths, usePublicCurrencies, useSignupQry } from 'mmfintech-backend-api'
import type { Currency, MerchantEntityType } from 'mmfintech-backend-api/types'
import {
  findCurrenciesByCountry,
  GlobalContext,
  isEmptyString,
  isSingleItemArray,
  isValidFunction,
  tr,
  useFormValues,
  useMatchMedia
} from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import settings from '../../settings'
import { CoreButton } from '../CoreButton'
import { CoreCheckbox } from '../CoreCheckbox'
import { CoreInput } from '../CoreInput'
import { CoreSelect } from '../CoreSelect'
import { CustomCheckbox } from '../CustomCheckbox'
import { PasswordTerms } from '../PasswordTerms'
import { ProductForm } from './ProductForm'
import styles from './RegisterForm.module.scss'
import { SoleTraderInfoModal } from './SoleTraderInfoModal'

interface RegisterFormProps {
  campaign?: string
}

const stepTitle = {
  1: tr('FRONTEND.SIGNUP.STEP_TITLE_1', 'About yourself'),
  2: tr('FRONTEND.SIGNUP.STEP_TITLE_2', 'Select product')
}

const stepSubtitle = {
  1: tr('FRONTEND.SIGNUP.STEP_SUBTITLE_1', 'Tell us about yourself!'),
  2: tr('FRONTEND.SIGNUP.STEP_SUBTITLE_2', 'Select product')
}

export const RegisterForm = ({ campaign }: RegisterFormProps) => {
  const { modalShow } = useContext(GlobalContext)

  const [step, setStep] = useState(1)
  const [agree, setAgree] = useState(false)
  const [country, setCountry] = useState(null)
  const [passwordFocused, setPasswordFocused] = useState<boolean>(false)

  // Honeypot fields for bot detection
  const [confirmPassword, setConfirmPassword] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')

  const isMobile = useMatchMedia({ breakpoint: breakpoints.sm, prefix: 'max' })

  const { currencies } = usePublicCurrencies()
  const { countryOptions, countriesError, signup, signupError, signupFetching, formTokenData, metrics } = useSignupQry(
    i18n.language
  )

  const formValues = useFormValues({
    countryCode: { required: true },
    email: { required: true, validation: 'email' },
    password: { required: true, validation: 'password', maxLength: 30 },
    entityType: { required: true },
    currency: {},
    contactPhone: { validation: 'phone', required: true },
    name: { required: true, validation: 'name' },
    isSoleTrader: { value: false }
  })

  const productFormValues = useFormValues({
    bankingServices: { value: false },
    paymentServices: { value: false },
    other: { value: false },
    otherValue: { validation: 'safe-string' },
    heardAboutUs: { required: true },
    expectedDepositVolume: { required: false }
  })

  const isIndividualSelected = () => formValues.getValue('entityType') === MerchantEntityTypes.INDIVIDUAL
  const isCompanySelected = () => formValues.getValue('entityType') === MerchantEntityTypes.COMPANY

  const handleEntityTypeChange = (entityType: MerchantEntityType) => {
    formValues.setValue('entityType', entityType)
    // Sole Trader only applies to corporate accounts - reset when switching away
    if (entityType !== MerchantEntityTypes.COMPANY) {
      formValues.setValue('isSoleTrader', false)
    }
  }

  // Sole Trader is a confirmation-gated choice: checking it opens an explainer
  // modal, and the value is only set once the user confirms. Unchecking is direct.
  const handleSoleTraderClick = (checked: boolean) => {
    if (!checked) {
      formValues.setValue('isSoleTrader', false)
      return
    }
    modalShow({
      header: tr('FRONTEND.SIGNUP.SOLE_TRADER.MODAL_TITLE', 'Are you a Sole Trader?'),
      options: { size: 'medium' },
      content: <SoleTraderInfoModal onConfirm={() => formValues.setValue('isSoleTrader', true)} />
    })
  }

  const handleContinueClick = () => {
    if (formValues.areValid()) {
      setStep(2)
    }
  }

  const handleCountryChange = (_: any, value: any) => {
    formValues.setValue('countryCode', value)
    setCountry(value)
  }

  const handleSignupSubmit = () => {
    productFormValues.setRequired('expectedDepositVolume', isIndividualSelected())

    if (formValues.areValid() && productFormValues.areValid()) {
      void signup(
        {
          ...formValues.prepare(),
          isSoleTrader: isCompanySelected() && formValues.getValue('isSoleTrader'),
          confirmPassword,
          confirmEmail,
          products: {
            bankingServices: productFormValues.getValue('bankingServices'),
            paymentServices: productFormValues.getValue('paymentServices'),
            heardAboutUs: productFormValues.getValue('heardAboutUs'),
            ...(isIndividualSelected
              ? {
                  expectedDepositVolume: productFormValues.getValue('expectedDepositVolume')
                }
              : {}),
            other: productFormValues.getValue('otherValue'),
            ...(campaign ? { campaign } : {})
          }
        },
        settings
      )
    }
  }

  const handleSubmitClick = () => {
    if (productFormValues.areValid()) {
      if (isValidFunction(handleSignupSubmit)) {
        void handleSignupSubmit()
      }
    }
  }

  useEffect(() => {
    return () => {
      setStep(1)
    }
  }, [])

  useEffect(() => {
    if (country) {
      const extractedCurrency = findCurrenciesByCountry(country)[0]
      const found = currencies.find(
        (c: Currency) => c.supportedForPrimary && c.type === 'FIAT' && c.currencyCode === extractedCurrency
      )
      if (found) {
        formValues.setValue('currency', found.currencyCode)
      }
    }
  }, [country])

  useEffect(() => {
    if (signupError) {
      formValues.handleErrors(signupError)
      setStep(1)
    }
  }, [signupError])

  useEffect(() => {
    if (isSingleItemArray(countryOptions) && isEmptyString(formValues.getValue('countryCode'))) {
      formValues.setValue('countryCode', countryOptions[0]?.value)
    }
  }, [countryOptions])

  // Set honeypot confirmEmail to nonce from form token
  useEffect(() => {
    if (formTokenData?.token) {
      const parsed = parseFormToken(formTokenData.token)
      if (parsed?.nonce) {
        setConfirmEmail(parsed.nonce)
      }
    }
  }, [formTokenData])

  const validData = () =>
    formValues.getValue('countryCode') !== '' &&
    formValues.getValue('email') !== '' &&
    formValues.getValue('password') !== '' &&
    formValues.getValue('entityType') !== '' &&
    formValues.getValue('contactPhone') !== ''

  // useAnalytics({
  //   enabled: configuration.isProduction(),
  //   script: `
  //     twq('event', 'tw-op1sp-opj4f', {
  //       email_address: ${formValues.getValue('email') ? `'${formValues.getValue('email')}'` : 'null'},
  //       phone_number: ${formValues.getValue('contactPhone') ? `'${fixPhoneNumber(formValues.getValue('contactPhone'))}'` : 'null'}
  //     });
  //   `
  // })

  const replaceUrl = (text: string, search: string, replacement: string): string => {
    const list = text.split(search)
    return list.length > 1 ? list.join(`<a href="${replacement}" target='_blank' rel='noreferrer'>`) : text
  }

  const prepareAgreement = (): string => {
    const original = tr(
      'FRONTEND.SIGNUP.AGREEMENT',
      'I confirm that I have read and agree with <a data-type="terms-and-conditions">Terms and Conditions</a>, <a data-type="terms-of-use">Terms of Use</a> and <a data-type="privacy-policy">Privacy Policy</a>.'
    )
    return replaceUrl(
      replaceUrl(
        replaceUrl(
          original,
          '<a data-type="terms-and-conditions">',
          'https://www.thekingdombank.com/terms-and-conditions'
        ),
        '<a data-type="privacy-policy">',
        'https://www.thekingdombank.com/privacy-policy'
      ),
      '<a data-type="terms-of-use">',
      'https://www.thekingdombank.com/terms-of-use'
    )
  }

  return (
    <div className={styles['register-form-wrapper']}>
      <div className='register-form-title'>
        <h2>{tr('FRONTEND.SIGNUP.NEW_TITLE', 'Sign up')}</h2>
        <CoreSteps step={step} maxStep={2} title={isMobile ? null : stepTitle[step]} />
      </div>

      <div className='subtitle'>{stepSubtitle[step]}</div>

      {step === 1 && (
        <div>
          <form noValidate>
            {/* Honeypot fields - hidden from humans, visible to bots */}
            <div className='hp-container' aria-hidden='true'>
              <input
                className='hp-field'
                type='text'
                name='confirm_password'
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                tabIndex={-1}
                autoComplete='off'
              />
              <input
                className='hp-field'
                type='email'
                name='confirm_email'
                value={confirmEmail}
                onChange={e => setConfirmEmail(e.target.value)}
                tabIndex={-1}
                autoComplete='off'
              />
            </div>
            <div className='form-inputs'>
              <div className='entityType-container'>
                <CustomCheckbox
                  icon={<CompanyIcon />}
                  caption={tr('FRONTEND.SIGNUP.ACCOUNT_TYPES.COMPANY', 'Corporate Account')}
                  checked={isCompanySelected()}
                  toggle={() => handleEntityTypeChange(MerchantEntityTypes.COMPANY)}
                  dataTest='account-type'
                />
                <CustomCheckbox
                  icon={<IndividualIcon />}
                  caption={tr('FRONTEND.SIGNUP.ACCOUNT_TYPES.INDIVIDUAL', 'Individual Account')}
                  checked={isIndividualSelected()}
                  toggle={() => handleEntityTypeChange(MerchantEntityTypes.INDIVIDUAL)}
                  dataTest='account-type'
                />
              </div>
              {isCompanySelected() && (
                <CoreCheckbox
                  type='check'
                  value='sole-trader'
                  className='sole-trader-checkbox'
                  checked={formValues.getValue('isSoleTrader')}
                  onClick={handleSoleTraderClick}
                  dataTest='sole-trader'
                  label={
                    <span className='sole-trader-label'>
                      <span className='sole-trader-title'>
                        {tr('FRONTEND.SIGNUP.SOLE_TRADER.TITLE', 'Sole Trader')}
                      </span>
                      <span className='sole-trader-description'>
                        {tr(
                          'FRONTEND.SIGNUP.SOLE_TRADER.DESCRIPTION_SUBTITLE',
                          'Select this if you operate your business as an individual, do not have a registered company, and you and your business are legally the same person.'
                        )}
                      </span>
                    </span>
                  }
                />
              )}
              <CoreInput
                name='name'
                type='text'
                data-test='name'
                label={
                  isIndividualSelected()
                    ? tr('FRONTEND.REGISTER_FORM.YOUR_NAME', 'Your name')
                    : isCompanySelected()
                      ? tr('FRONTEND.QUESTIONNAIRE.KYB.TRADING_NAME', 'Company Trading Name')
                      : tr('FRONTEND.REGISTER_FORM.YOUR_NAME_COMPANY_TRADING_NAME', 'Your name / Company trading name')
                }
                {...formValues.registerInput('name')}
                disabled={signupFetching || !formValues.getValue('entityType').length}
                autoComplete='off'
                className='register-form-input-company-name'
                onFocus={metrics.onFocus}
                onKeyDown={metrics.onKeyDown}
                onPaste={metrics.onPaste}
              />
              <div className='register-form-inputs'>
                {countryOptions.length > 1 ? (
                  <CoreSelect
                    type='country'
                    name='countryCode'
                    data-test='country-code'
                    label={tr('FRONTEND.SIGNUP.COUNTRY_LABEL', 'Country')}
                    value={formValues.getValue('countryCode')}
                    onChange={handleCountryChange}
                    options={countryOptions}
                    disabled={signupFetching || !formValues.getValue('entityType').length}
                  />
                ) : (
                  <CoreSelect
                    type='country'
                    data-test='country-code'
                    label={tr('FRONTEND.SIGNUP.COUNTRY_LABEL', 'Country')}
                    value={countryOptions?.find((c: any) => c?.value === formValues.getValue('countryCode'))?.label}
                    required={formValues.isRequired('countryCode')}
                    disabled={signupFetching || !formValues.getValue('entityType').length}
                  />
                )}
                <CoreInput
                  name='phone'
                  type='phone'
                  data-test='contact-phone'
                  label={tr('FRONTEND.SIGNUP.PHONE', 'Contact phone')}
                  {...formValues.registerInput('contactPhone')}
                  disabled={signupFetching || !formValues.getValue('entityType').length}
                  autoComplete='off'
                  onFocus={metrics.onFocus}
                  onKeyDown={metrics.onKeyDown}
                  onPaste={metrics.onPaste}
                />
                <CoreInput
                  name='email'
                  type='email'
                  data-test='email'
                  label={tr('FRONTEND.SIGNUP.EMAIL', 'Email')}
                  maxLength={320}
                  {...formValues.registerInput('email')}
                  disabled={signupFetching || !formValues.getValue('entityType').length}
                  autoComplete='off'
                  onFocus={metrics.onFocus}
                  onKeyDown={metrics.onKeyDown}
                  onPaste={metrics.onPaste}
                />

                <CoreInput
                  name='password'
                  type='password'
                  data-test='password'
                  label={tr('FRONTEND.SIGNUP.PASSWORD', 'Password')}
                  {...formValues.registerInput('password')}
                  disabled={signupFetching || !formValues.getValue('entityType').length}
                  autoComplete='off'
                  focused={passwordFocused}
                  setFocus={setPasswordFocused}
                  maxLength={30}
                  onFocus={metrics.onFocus}
                  onKeyDown={metrics.onKeyDown}
                  onPaste={metrics.onPaste}
                />
                {passwordFocused && <PasswordTerms password={formValues.getValue('password')} />}
              </div>
            </div>
          </form>
        </div>
      )}

      {step === 2 && (
        <ProductForm isIndividualSelected={isIndividualSelected()} productFormValues={productFormValues} />
      )}

      <div className='form-actions'>
        <ErrorDisplay error={[countriesError, signupError]} />

        {step === 2 && (
          <div className='user-agreement'>
            <CoreCheckbox type='check' value='agreement' checked={agree} onClick={setAgree} />
            <div>{parse(prepareAgreement())}</div>
          </div>
        )}
        <CoreButton
          type='button'
          text={
            step === 1 ? tr('FRONTEND.BUTTONS.CONTINUE', 'Continue') : tr('FRONTEND.SIGNUP.BUTTON_CREATE', 'Sign up')
          }
          disabled={
            step === 1
              ? signupFetching || !validData()
              : (!productFormValues.getValue('bankingServices') &&
                  !productFormValues.getValue('paymentServices') &&
                  !productFormValues.getValue('other')) ||
                (isIndividualSelected() && !productFormValues.getValue('expectedDepositVolume')) ||
                !agree
          }
          isLoading={signupFetching}
          onClick={step === 1 ? handleContinueClick : handleSubmitClick}
          data-test='button-submit'
          fullWidth
          size='large'
        />

        <div data-test='dont-have-account-test' className='auth-signup-link'>
          {tr('FRONTEND.LOGIN.YOU_HAVE_AN_ACCOUNT', 'Already have an account?')}
          <Link to={paths.login()} data-test='sign-up'>
            <span className='auth-signup-link-text'>
              {tr('FRONTEND.LOGIN.SIGNIN_BUTTON', 'Sign in')} <ArrowIcon />
            </span>
          </Link>
        </div>
      </div>
    </div>
  )
}
